import { ChangeEvent, useCallback, useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { BookIcon, CircleHelpIcon, InfoIcon } from 'lucide-react';
import { Resolver, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import OptionRadioButton from '@/components/atoms/OptionRadioButton/OptionRadioButton';
import ToolkitWrapper from '@/components/organisms/Toolkit/ToolkitWrapper';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import useDialogAlert from '@/hooks/useDialogAlert';
import useFocusOnInput from '@/hooks/useFocusOnInput';
import { selectNodeIdByActionId, selectSelectedAction } from '@/redux/dialogs/selectors';
import { updateAction } from '@/redux/nodes/actions';
import { Action } from '@/shared/types/action';
import { RootState } from '@/shared/types/state';
import { REPLAY_RESPONSE_DOCS_URL } from '@/util/constants';
import { replayActionSchema } from '@/util/validation/schemas/action-schemas';

const REPLAY_OPTIONS = { DEFAULT: 'default', CUSTOM: 'custom' } as const;

type ReplayForm = { replay: string; customMessage?: string };

const ToolkitActionReplay = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const {
    actionId,
    texts: customMessage,
    type,
  } = useSelector((state: RootState) => {
    const selectedAction = selectSelectedAction(state) as Extract<Action, { type: 'replay' }>;
    return {
      actionId: selectedAction.action_id,
      texts: selectedAction.texts,
      type: selectedAction.type,
    };
  }, shallowEqual);
  const parentNodeId = useSelector(selectNodeIdByActionId(actionId));

  const replayOptions = [
    { id: 0, value: REPLAY_OPTIONS.DEFAULT, label: t('actions.replay.options.default') },
    { id: 1, value: REPLAY_OPTIONS.CUSTOM, label: t('actions.replay.options.custom') },
  ];

  const form = useForm<ReplayForm>({
    mode: 'onChange',
    defaultValues: {
      replay: customMessage ? REPLAY_OPTIONS.CUSTOM : REPLAY_OPTIONS.DEFAULT,
      customMessage: customMessage?.[0],
    },
    resolver: yupResolver(replayActionSchema) as Resolver<ReplayForm>,
  });
  const {
    control,
    trigger,
    setFocus,
    setValue,
    formState: { dirtyFields, errors },
  } = form;

  const watchReplayOption = useWatch({ control, name: 'replay' });

  const updateReplayAction = useCallback(
    (text?: string) => {
      // undefined clears the custom message entirely (default option selected),
      // while an empty string keeps the custom option active with no text yet
      const texts = text === undefined ? undefined : [text].filter(Boolean);

      dispatch(updateAction({ actionId, action: { texts } }));
    },
    [actionId, dispatch]
  );

  const handleReplayOptionChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setValue('replay', e.target.value, { shouldValidate: true, shouldDirty: true });
      const isDefault = e.target.value === REPLAY_OPTIONS.DEFAULT;
      const text = isDefault ? undefined : t('actions.generate_text', { 0: '' });

      setValue('customMessage', text, { shouldValidate: true });
      updateReplayAction(text);
    },
    [setValue, t, updateReplayAction]
  );

  useEffect(() => {
    trigger();
  }, [trigger]);

  useFocusOnInput('replay', errors, setFocus);

  const { setFieldError } = useDialogAlert({
    id: actionId,
    nodeId: parentNodeId,
    title: t('actions.types.replay'),
    type,
  });

  useEffect(() => {
    if (Object.keys(dirtyFields).length > 0) {
      setFieldError('customMessage', errors.customMessage?.message);
    }
  }, [dirtyFields, errors.customMessage?.message, setFieldError]);

  return (
    <ToolkitWrapper type="replay">
      <Form {...form}>
        <ContextualHelp
          preventClose
          name="action-replay"
          title={t('actions.replay.title')}
          links={[
            { label: t('docs.docs'), url: REPLAY_RESPONSE_DOCS_URL, icon: <BookIcon size={16} /> },
          ]}
        >
          {t('actions.replay.body_ai_agent')}
        </ContextualHelp>

        {replayOptions.map((option) => (
          <div className="mb-6" key={`${option.id}-${option.value}`}>
            <OptionRadioButton
              option={option}
              size="medium"
              name="replay"
              isSelected={watchReplayOption === option.value}
              onChange={handleReplayOptionChange}
            />
          </div>
        ))}
        {watchReplayOption === REPLAY_OPTIONS.CUSTOM && (
          <>
            <div className="my-2">
              <Alert variant="info">
                <InfoIcon size={16} />
                <AlertDescription>{t('actions.replay.banner_text')}</AlertDescription>
              </Alert>
            </div>

            <FormField
              control={control}
              name="customMessage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1">
                    {t('actions.replay.custom_message_label')}
                    <InfoTooltip
                      content={t('actions.replay.custom_message_tooltip')}
                      learnMoreUrl={REPLAY_RESPONSE_DOCS_URL}
                      side="right"
                    >
                      <CircleHelpIcon className="size-3.5 text-muted-foreground" />
                    </InfoTooltip>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      autoFocus
                      placeholder={t('actions.replay.custom_message_placeholder')}
                      onChange={(event) => {
                        field.onChange(event);
                        updateReplayAction(event.target.value);
                      }}
                      onBlur={(event) => {
                        const trimmed = event.target.value.trim();
                        if (trimmed !== event.target.value) {
                          field.onChange(trimmed);
                          updateReplayAction(trimmed);
                        }
                        field.onBlur();
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}
      </Form>
    </ToolkitWrapper>
  );
};

export default ToolkitActionReplay;
